import { useEffect, useMemo, useRef } from 'react';
import { ViewPreset } from '@bryntum/schedulerpro';
import { BryntumSchedulerProProps } from '@bryntum/schedulerpro-react';
import { ColumnsState } from '../types/scheduler.types';
import { ResourceInfoCell } from '../components/ResourceInfoCell';
import {
  CapacityScheduler,
  capacityAxisFrom,
  readClaimOwnerCapacity,
  renderCapacityHost,
} from '../components/CapacityBand';
import { capacityTooltipText } from '../components/CapacityTooltip';

/**
 * Payload for schedule tooltip beforeShow event
 */
interface ScheduleTooltipBeforeShowPayload {
  source?: {
    activeTarget?: HTMLElement;
  };
}

/**
 * `this` inside a ScheduleTooltip config function.
 *
 * Bryntum assigns the feature's config onto the feature instance, so `getText`
 * runs with the feature as `this` and `client` is the scheduler. Optional
 * throughout: if it is ever absent the axis resolves to null and the tooltip
 * falls back to Bryntum's default readout, which is the correct degradation.
 */
interface ScheduleTooltipHost {
  client?: CapacityScheduler;
}

/**
 * The DOM event Bryntum hands to `getText`. Only `target` is needed — see
 * CapacityTooltip: a separately tracked pointer is stale by the time this
 * runs, because Bryntum's own mousemove handler goes first.
 */
interface ScheduleTooltipHoverEvent {
  target?: unknown;
}

/**
 * Resource record data from Bryntum
 */
interface ResourceRecord {
  id: string;
  name: string;
  description?: string;
  isStandby?: boolean;
  [key: string]: unknown;
}

/**
 * Renderer params from Bryntum column renderer
 */
interface RendererParams {
  record: ResourceRecord;
  value: string;
  column: {
    avatarRendering?: {
      getResourceAvatar: (record: ResourceRecord) => { text: string };
    };
  };
}

/**
 * Renderer params from Bryntum's RowExpander feature.
 *
 * Called ONCE PER GRID REGION, which is what the capacity band relies on: the
 * labels go in the locked region, the figures are projected onto the time axis
 * in the normal region.
 */
interface RowExpanderRenderData {
  record: ResourceRecord;
  region: string;
  grid: CapacityScheduler;
}

interface UseSchedulerConfigOptions {
  /** Event category options for the task edit dropdown */
  eventCategories: string[];
  /** Column visibility state for resource grid columns */
  columnsState: ColumnsState;
  /** Whether user has permission to create events (drag-to-create) */
  canCreateEvents?: boolean;
  /** Whether current user is an adjuster (affects avatar navigation) */
  isAdjuster?: boolean;
  /**
   * The board's selected time zone. The whole axis is that zone's wall clock
   * (the API adjusts every datetime and Bryntum is deliberately not given a
   * `timeZone`), so it is also the zone the capacity band must resolve "today"
   * in — reading the browser's calendar instead mislabels a column past or
   * not-past, and a past column shows no figures.
   */
  timeZone?: string;
}

/**
 * Helper to get initials from a name
 */
function getInitials(name: string): string {
  if (!name) return '';
  const parts = name.trim().split(/\s+/);
  if (parts.length === 1) {
    return parts[0].substring(0, 2).toUpperCase();
  }
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

/**
 * Hook to generate Bryntum SchedulerPro configuration
 */
export function useSchedulerConfig({
  columnsState,
  eventCategories = [],
  canCreateEvents = true,
  isAdjuster = false,
  timeZone,
}: UseSchedulerConfigOptions) {
  /**
   * The board's selected zone, readable from a FROZEN closure.
   *
   * `SchedulerCore` adopts this config once — Bryntum's config is immutable
   * after first render — so every closure below is built on the first render's
   * `timeZone` and keeps it forever, however often this memo re-runs. That
   * matters because `buildCapacityModel` resolves "today" through the zone it is
   * handed, which decides `isToday`, `isPast`, `hasFutureDays`, `containsToday`,
   * `week.remaining` and `usedEarlierThisWeek`.
   *
   * The band survives that because `refreshCapacityBands` rewrites every mounted
   * host from a live-zone effect. The tooltip has no such path: nothing
   * re-invokes `getText`, so a stale zone there would make the SAME cell answer
   * differently on hover than it renders — the one place the shared model cannot
   * prevent the two surfaces disagreeing, because the two callers hand
   * `buildCapacityModel` different zones.
   *
   * A ref fixes both structurally: its identity is stable, so a frozen closure
   * holding it still reads the current value. Same reason `SchedulerCore` keeps
   * `displayWindowRef`.
   */
  const timeZoneRef = useRef(timeZone);
  useEffect(() => {
    timeZoneRef.current = timeZone;
  }, [timeZone]);

  return useMemo(() => {
    // Custom view preset for Dispatch - matches original scheduler
    // Note: shiftIncrement must match defaultSpan to avoid gaps when shifting
    const viewPreset: Partial<ViewPreset> = {
      id: 'dispatchViewPreset',
      name: 'Dispatch View Preset',
      base: 'hourAndDay',
      shiftIncrement: 7,
      shiftUnit: 'day',
      mainUnit: 'day',
      defaultSpan: 7,
      timeResolution: {
        unit: 'minute',
        increment: 1,
      },
    };

    // Custom resource cell renderer with clickable avatar
    const resourceRenderer = ({ record, value }: RendererParams) => {
      const initials = getInitials(value || record.name || '');
      return (
        <ResourceInfoCell
          resourceId={record.id}
          name={value || record.name || ''}
          initials={initials}
          description={record.description}
          isStandby={record.isStandby}
          isAdjuster={isAdjuster}
        />
      );
    };

    // Resource columns - use 'as const' for Bryntum v7 type compatibility
    const columns = [
      {
        type: 'resourceInfo' as const,
        text: 'Resource',
        field: 'name',
        width: 200,
        showImage: false,
        hidden: columnsState['resource']?.hidden ?? false,
        renderer: resourceRenderer,
      },
      {
        text: 'Team',
        field: 'team',
        width: 150,
        hidden: columnsState['team']?.hidden ?? false,
      },
      {
        text: 'County',
        field: 'county',
        width: 150,
        hidden: columnsState['county']?.hidden ?? true,
      },
    ];

    const features = {
      dependencies: { disabled: true },
      eventBuffer: {
        disabled: false,
        // V1 parity: display "Travel Time: {duration}" when hovering a
        // preamble/postamble segment. V1 also suppressed the tooltip for
        // specific classTo cases — that tenant signal doesn't exist in V2,
        // so we show the duration unconditionally.
        tooltipTemplate: ({ duration }: { duration: string }) =>
          `Travel Time: ${duration}`,
      },
      cellEdit: { disabled: true },
      nonWorkingTime: { disabled: false },
      resourceNonWorkingTime: {
        enableMouseEvents: true,
      },
      // Hide schedule tooltip when hovering over non-working time (we have our own tooltip for that)
      scheduleTooltip: {
        /**
         * Claim Owner capacity on hover (ADO #104914, AC 28).
         *
         * `getText`, NOT `generateTipContent`. Bryntum builds the tooltip as
         * `clockHtml + messageHtml`, where the message is whatever `getText`
         * returns; overriding `generateTipContent` replaces both and takes the
         * default date readout with it. The criterion wants the capacity
         * figures ALONGSIDE that readout, so this is the hook.
         *
         * Scoping lives in `capacityTooltipText` and keys off the handed-in
         * `event.target`: over a capacity cell you get capacity, over the task
         * row you get the default readout and nothing else. An empty return
         * appends no message at all, which is what makes the negative case a
         * property of the code rather than of the CSS.
         *
         * Every figure comes from the band's own model — no second copy of the
         * `min(daily, weekly)` rule, and no time zone named: the dispatcher
         * chose it in the toolbar and the figures are already in it.
         */
        getText(
          this: ScheduleTooltipHost,
          date: Date,
          event: ScheduleTooltipHoverEvent | null | undefined,
          resourceRecord: ResourceRecord | null | undefined
        ): string {
          return capacityTooltipText({
            target: event?.target,
            resourceId: resourceRecord ? String(resourceRecord.id) : null,
            capacity: readClaimOwnerCapacity(resourceRecord),
            axis: capacityAxisFrom(this?.client),
            // `today` is left to buildCapacityModel's own default, exactly as
            // the band's renderer leaves it; the zone is what makes that "now"
            // the board's rather than the browser's.
            //
            // Read through the ref, NOT the closed-over `timeZone`: this closure
            // is captured on first render and never rebuilt, and nothing
            // re-invokes `getText` after a zone change, so the literal would
            // pin the tooltip to the zone the board opened in while the band
            // moved on.
            timeZone: timeZoneRef.current,
          });
        },
        listeners: {
          beforeShow({ source }: ScheduleTooltipBeforeShowPayload) {
            // Check if hovering over non-working time element
            const target = source?.activeTarget;
            if (target?.closest('.b-sch-resource-non-working-time')) {
              return false; // Prevent tooltip for non-working time
            }
            return true; // Allow tooltip for working time
          },
        },
      },
      timeSpanHighlight: { disabled: false },
      eventResize: { disabled: false },
      eventDrag: { disabled: false },
      eventDragCreate: { disabled: !canCreateEvents },
      eventTooltip: {
        disabled: false,
      },
      taskEdit: {
        disabled: false,
        editorConfig: {
          title: 'Blocked Time',
          tabBar: false,
          // Limit initial height to 75% of viewport, still resizable
          maxHeight: '75vh',
          // Allow click-outside to close the editor
          autoClose: true,
          modal: false,
          listeners: {
            beforeShow({
              source,
            }: {
              source: {
                widgetMap: Record<
                  string,
                  { value?: unknown; disabled?: boolean }
                >;
              };
            }) {
              const editor = source;
              const startDateField = editor.widgetMap['startDateField'];
              const endDateField = editor.widgetMap['endDateField'];
              const isAllDayField = editor.widgetMap['isAllDay'];

              // Auto-check isAllDay if duration is >= 23:59
              if (
                startDateField?.value &&
                endDateField?.value &&
                isAllDayField
              ) {
                const start = new Date(
                  startDateField.value as string | number | Date
                );
                const end = new Date(
                  endDateField.value as string | number | Date
                );
                const diffMs = end.getTime() - start.getTime();
                const DAY_IN_MS = 86340000; // 23 hours, 59 minutes

                if (diffMs >= DAY_IN_MS) {
                  isAllDayField.value = true;
                }
              }

              // Disable Save until a category is chosen. Bryntum's
              // `required: true` on the combo only flags the field as
              // invalid; the Save button stays enabled and the user can
              // still click through. We mirror that validity onto the
              // button itself so the action is gated, not just nagged.
              const saveButton = editor.widgetMap['saveButton'];
              const categoryField = editor.widgetMap['categoryField'];
              if (saveButton) {
                saveButton.disabled = !categoryField?.value;
              }
            },
          },
        },
        items: {
          generalTab: {
            labelPosition: 'above',
            layout: {
              type: 'vbox',
            },
            items: {
              percentDoneField: false,
              postambleField: false,
              preambleField: false,
              nameField: {
                type: 'text',
                name: 'name',
                label: 'Name',
                labelPosition: 'above',
                weight: 50,
              },
              startDateField: {
                labelPosition: 'above',
              },
              endDateField: {
                labelPosition: 'above',
              },
              categoryField: {
                type: 'combo',
                name: 'category',
                label: 'Category',
                items: eventCategories,
                weight: 400,
                required: true,
                // Keep the Save button in sync with the combo's value so
                // a user can't click Save while the field is still empty.
                // The combo lives inside the form which lives inside the
                // editor; saveButton sits on the editor's bbar — walk up
                // the widget tree until we find it.
                onChange({
                  value,
                  source,
                }: {
                  value: unknown;
                  source: {
                    parent?: unknown;
                  };
                }) {
                  type WidgetNode = {
                    widgetMap?: Record<string, { disabled?: boolean }>;
                    parent?: WidgetNode;
                  };
                  let node: WidgetNode | undefined = source as WidgetNode;
                  while (node) {
                    const saveButton = node.widgetMap?.['saveButton'];
                    if (saveButton) {
                      saveButton.disabled = !value;
                      return;
                    }
                    node = node.parent;
                  }
                },
              },
              isAllDay: {
                type: 'checkbox',
                name: 'isAllDay',
                label: 'All Day',
                labelPosition: 'before',
                weight: 550,
                onChange({
                  value,
                  source,
                }: {
                  value: boolean;
                  source: {
                    parent: {
                      widgetMap: Record<
                        string,
                        { timeField?: { hidden: boolean } }
                      >;
                    };
                  };
                }) {
                  const editor = source.parent;
                  const startDateField = editor.widgetMap['startDateField'];
                  const endDateField = editor.widgetMap['endDateField'];

                  if (startDateField?.timeField) {
                    startDateField.timeField.hidden = value;
                  }
                  if (endDateField?.timeField) {
                    endDateField.timeField.hidden = value;
                  }
                },
              },
              isForced: {
                type: 'checkbox',
                name: 'isForced',
                label: 'Force',
                labelPosition: 'before',
                weight: 600,
              },
              // Created by field (read-only)
              createdBy: {
                type: 'text',
                name: 'createdBy',
                label: 'Created By',
                weight: 650,
                readOnly: true,
              },
              // Edited by field (read-only)
              editedBy: {
                type: 'text',
                name: 'editedBy',
                label: 'Edited By',
                weight: 660,
                readOnly: true,
              },
              // Notes textarea
              notesField: {
                cls: 'menu-text-field',
                type: 'textArea',
                name: 'notes',
                label: 'Notes',
                weight: 1000,
              },
            },
          },
          // Disable unused Bryntum default tabs
          predecessorsTab: false,
          successorsTab: false,
          notesTab: false,
          advancedTab: false,
        },
      },
      // Context menu configuration
      eventMenu: {
        items: {
          cutEvent: false,
          copyEvent: false,
          splitEvent: false,
        },
        processItems({
          eventRecord,
          items,
        }: {
          eventRecord?: { data?: { taskReferenceId?: string } };
          items: Record<string, unknown>;
        }) {
          const taskReferenceId = eventRecord?.data?.taskReferenceId;
          if (taskReferenceId && taskReferenceId !== '') {
            // Task events: hide delete (only blocked time events can be deleted)
            if (items['deleteEvent']) items['deleteEvent'] = false;
          }
        },
      },
      scheduleMenu: {
        disabled: !canCreateEvents,
      },
      // Disable remove/delete options for resource rows and columns
      cellMenu: {
        items: {
          removeRow: false,
        },
      },
      headerMenu: {
        disabled: false,
        items: {
          removeRow: false,
          removeColumn: false,
        },
      },
      timeAxisHeaderMenu: { disabled: false },
      /**
       * Claim Owner capacity band on the resource row (ADO #104914).
       *
       * Off and collapsed on every board load, so Scheduler View keeps its
       * current layout until a dispatcher turns the toolbar's capacity toggle
       * on and then opens a row. The renderer runs once per
       * region: labels in the locked region, figures projected onto the time
       * axis in the normal region. Both halves are wrapped in a host tagged
       * with the record id so a range or zoom change can rewrite the figures
       * in place — RowExpander caches its body elements per record, so
       * collapsing and re-expanding must not be the refresh mechanism.
       */
      rowExpander: {
        columnPosition: 'first' as const,
        // The capacity toggle starts OFF on every board load, so the feature is
        // configured inert: `disabled` blocks expansion and `column.hidden`
        // keeps the expander control out of the grid from the very first paint.
        // Both are needed — Bryntum applies `disabled` during configuration,
        // before `addColumn()` has created the column its `updateDisabled` hook
        // hides, so relying on `disabled` alone would flash a live expander
        // control on load. SchedulerCore flips `disabled` on the live instance
        // (Bryntum's own `updateDisabled` then shows/hides the column), which
        // is the only path that works: this config object is captured once on
        // first render and never re-read.
        disabled: true,
        column: { hidden: true },
        enableAnimations: false,
        // Re-render when the resource record itself changes, so a refetched
        // capacity payload replaces the figures without a collapse/expand.
        refreshOnRecordChange: true,
        renderer: ({ record, region, grid }: RowExpanderRenderData) =>
          renderCapacityHost(
            String(record.id),
            region,
            readClaimOwnerCapacity(record),
            capacityAxisFrom(grid),
            // `today` defaults to now; the zone is what makes that "now" the
            // board's, not the browser's.
            //
            // Through the ref for the same reason as the tooltip above. Today
            // this is belt-and-braces rather than a fix: this renderer is also
            // frozen, but a fresh expand fires `rowExpand`, which triggers the
            // in-place refresh that rewrites the host with the live zone a frame
            // later. Reading live here means the first paint is already correct
            // and the band no longer depends on that refresh to mask a stale
            // capture.
            undefined,
            timeZoneRef.current
          ),
      },
      // Current time line indicator - uses Bryntum built-in with timezone support
      timeRanges: {
        showCurrentTimeLine: true,
        currentTimeLineUpdateInterval: 10000, // 10 seconds
        currentDateFormat: 'hh:mm a',
      },
    };

    const keyMap: Record<string, string> = {
      shiftPrevious: '',
      shiftNext: '',
    };

    const subGridConfigs = {
      locked: {
        width: 530,
      },
      normal: {
        flex: 1,
      },
    };

    return {
      viewPreset,
      columns,
      features,
      subGridConfigs,
      keyMap,
      allowOverlap: false,
      barMargin: 2,
      resourceMargin: 2,
      rowHeight: 50,
      autoHeight: false,
      eventStyle: 'filled' as BryntumSchedulerProProps['eventStyle'],
    };
    // `timeZone` is deliberately NOT a dependency: nothing in this config reads
    // it directly any more, both consumers go through `timeZoneRef`. Listing it
    // would rebuild the whole config on every zone change only for
    // `SchedulerCore` to discard the result, which read as though the config
    // tracked the zone when it cannot.
  }, [columnsState, eventCategories, canCreateEvents, isAdjuster]);
}
